/**
 * Layer Control Panel
 *
 * Creates and manages the interactive layer control UI
 */

import { toggleLayerVisibility } from './main.js';
import { toggleSubdivisionLayerVisibility } from './subdivisionLayers.js';

/**
 * Initialize the layer control panel
 * @param {mapboxgl.Map} map - The map instance
 * @param {Array} layerGroups - Array of parcel layer groups
 * @param {Object} subdivisionLayerGroup - Subdivision layer group
 */
export function initializeLayerControl(map, layerGroups, subdivisionLayerGroup) {
  const panel = document.getElementById('layer-control-panel');
  if (!panel) {
    console.error('Layer control panel element not found');
    return;
  }

  // Clear existing content
  panel.innerHTML = '';

  // Add parcel layers section
  const parcelTitle = document.createElement('h3');
  parcelTitle.textContent = 'Parcel Layers';
  parcelTitle.style.cssText = 'margin: 0 0 15px 0; font-size: 16px; color: #333; border-bottom: 2px solid #0078A8; padding-bottom: 5px;';
  panel.appendChild(parcelTitle);

  // Create parcel layer groups
  layerGroups.forEach(group => {
    const groupDiv = createLayerGroup(map, group);
    panel.appendChild(groupDiv);
  });

  // Add subdivision layers section
  if (subdivisionLayerGroup) {
    // Add spacing
    const spacer = document.createElement('div');
    spacer.style.cssText = 'height: 20px;';
    panel.appendChild(spacer);

    // Add subdivision section
    const subdivDiv = createSubdivisionGroup(map, subdivisionLayerGroup);
    panel.appendChild(subdivDiv);
  }

  // Add legend toggle button
  const toggleButton = createToggleButton(panel);
  document.body.appendChild(toggleButton);
}

/**
 * Create a layer group section
 * @param {mapboxgl.Map} map - The map instance
 * @param {Object} group - Layer group configuration
 * @returns {HTMLElement} Group element
 */
function createLayerGroup(map, group) {
  const groupDiv = document.createElement('div');
  groupDiv.className = 'layer-county-group';
  groupDiv.style.cssText = 'margin-bottom: 12px;';

  // Group header
  const header = document.createElement('h4');
  header.textContent = `${group.county} County`;
  header.style.cssText = 'margin: 0 0 5px 0; font-size: 13px; font-weight: bold; color: #555;';
  groupDiv.appendChild(header);

  // Add checkboxes for each layer
  group.layers.forEach(layer => {
    const checkbox = createLayerCheckbox(map, layer);
    groupDiv.appendChild(checkbox);
  });

  return groupDiv;
}

/**
 * Create a checkbox for a layer
 * @param {mapboxgl.Map} map - The map instance
 * @param {Object} layer - Layer configuration
 * @returns {HTMLElement} Checkbox element
 */
function createLayerCheckbox(map, layer) {
  const div = document.createElement('div');
  div.className = 'layer-checkbox-item';
  div.style.cssText = 'margin-left: 10px; margin-bottom: 3px; font-size: 12px; display: flex; align-items: center;';

  // Color indicator
  const colorBox = document.createElement('span');
  colorBox.style.cssText = `
    display: inline-block;
    width: 14px;
    height: 14px;
    background-color: ${layer.style.fillColor};
    border: 1px solid ${layer.style.lineColor};
    margin-right: 5px;
    flex-shrink: 0;
  `;

  // Checkbox input
  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.id = `layer-${layer.id}`;
  checkbox.checked = layer.visible;
  checkbox.style.cssText = 'margin-right: 5px; cursor: pointer;';

  // Change handler
  checkbox.addEventListener('change', (e) => {
    toggleLayerVisibility(map, layer.id, e.target.checked);
  });

  // Label
  const label = document.createElement('label');
  label.htmlFor = checkbox.id;
  label.textContent = layer.distance;
  label.style.cssText = 'cursor: pointer; user-select: none;';

  // Assemble elements
  div.appendChild(colorBox);
  div.appendChild(checkbox);
  div.appendChild(label);

  return div;
}

/**
 * Create a subdivision layer group section
 * @param {mapboxgl.Map} map - The map instance
 * @param {Object} group - Subdivision layer group configuration
 * @returns {HTMLElement} Group element
 */
function createSubdivisionGroup(map, group) {
  const groupDiv = document.createElement('div');
  groupDiv.className = 'layer-subdivision-group';
  groupDiv.style.cssText = 'margin-bottom: 12px;';

  // Group header
  const header = document.createElement('h3');
  header.textContent = group.name;
  header.style.cssText = 'margin: 0 0 10px 0; font-size: 16px; color: #333; border-bottom: 2px solid #ff8c00; padding-bottom: 5px;';
  groupDiv.appendChild(header);

  // Add checkboxes for each layer
  group.layers.forEach(layer => {
    const checkbox = createSubdivisionCheckbox(map, layer);
    groupDiv.appendChild(checkbox);
  });

  return groupDiv;
}

/**
 * Create a checkbox for a subdivision layer
 * @param {mapboxgl.Map} map - The map instance
 * @param {Object} layer - Layer configuration
 * @returns {HTMLElement} Checkbox element
 */
function createSubdivisionCheckbox(map, layer) {
  const div = document.createElement('div');
  div.className = 'layer-checkbox-item';
  div.style.cssText = 'margin-left: 10px; margin-bottom: 5px; font-size: 12px; display: flex; align-items: center;';

  // Color indicator (circle)
  const colorCircle = document.createElement('span');
  colorCircle.style.cssText = `
    display: inline-block;
    width: 14px;
    height: 14px;
    background-color: ${layer.style.circleColor};
    border: 2px solid ${layer.style.circleStrokeColor};
    border-radius: 50%;
    margin-right: 5px;
    flex-shrink: 0;
  `;

  // Checkbox input
  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.id = `layer-${layer.id}`;
  checkbox.checked = layer.visible;
  checkbox.style.cssText = 'margin-right: 5px; cursor: pointer;';

  // Change handler
  checkbox.addEventListener('change', (e) => {
    toggleSubdivisionLayerVisibility(map, layer.id, e.target.checked);
  });

  // Label
  const label = document.createElement('label');
  label.htmlFor = checkbox.id;
  label.textContent = layer.name;
  label.style.cssText = 'cursor: pointer; user-select: none;';

  // Assemble elements
  div.appendChild(colorCircle);
  div.appendChild(checkbox);
  div.appendChild(label);

  return div;
}

/**
 * Create a toggle button to show/hide the panel
 * @param {HTMLElement} panel - The panel element
 * @returns {HTMLElement} Toggle button
 */
function createToggleButton(panel) {
  const button = document.createElement('button');
  button.id = 'layer-control-toggle';
  button.innerHTML = '☰ Layers';
  button.style.cssText = `
    position: absolute;
    top: 10px;
    right: 10px;
    background: white;
    border: none;
    padding: 10px 15px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.3);
    cursor: pointer;
    font-size: 14px;
    font-weight: bold;
    color: #333;
    z-index: 999;
    display: none;
  `;

  let panelVisible = true;

  button.addEventListener('click', () => {
    panelVisible = !panelVisible;
    panel.style.display = panelVisible ? 'block' : 'none';
    button.innerHTML = panelVisible ? '✕ Close' : '☰ Layers';
  });

  // Show toggle button on mobile
  if (window.innerWidth < 768) {
    button.style.display = 'block';
    panel.style.display = 'none';
    panelVisible = false;
  }

  // Handle window resize
  window.addEventListener('resize', () => {
    if (window.innerWidth < 768) {
      button.style.display = 'block';
      panel.style.display = panelVisible ? 'block' : 'none';
    } else {
      button.style.display = 'none';
      panel.style.display = 'block';
    }
  });

  return button;
}
